// ─────────────────────────────────────────────────────────────────────────────
// Ponto ÚNICO de emissão de eventos personalizados para o dataLayer (GTM/GA4).
//
// Nenhum componente ou store deve chamar `window.dataLayer.push` diretamente —
// todo evento passa por `pushEvent`, para que gating de ambiente, logging e
// parâmetros comuns (`product`) tenham um só lugar para mudar.
// ─────────────────────────────────────────────────────────────────────────────

const PRODUCT = "Litterae";

// Empurra um evento para o dataLayer. `product` é comum a todos os eventos do
// Litterae; params sobrescreve, caso algum evento precise de valor diferente.
function pushEvent(eventName, params = {}) {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: eventName,
    product: PRODUCT,
    ...params,
  });
}

// Converte para boolean apenas quando o valor é reconhecidamente booleano.
// Retorna undefined para ausente/indecifrável — nunca `false` por omissão, pois
// as regras de disparo usam comparação estrita e um `false` inventado geraria
// transição falsa (ver _handleFirstActivationEvents em useUserStore).
export function toBooleanFlag(value) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === 0) return value === 1;

  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "true" || s === "1") return true;
    if (s === "false" || s === "0") return false;
  }

  return undefined;
}

// Lê do payload de login o indicador de "conta recém-criada neste login".
// Aceita as grafias usuais para não depender da convenção exata do backend.
// Retorna true/false quando o backend informa, e undefined quando o campo não
// veio — caso em que o chamador NÃO deve tratar o login como cadastro.
export function resolveIsNewAccount(loginPayload) {
  if (!loginPayload) return undefined;

  const candidates = [
    loginPayload.isNewUser,
    loginPayload.IsNewUser,
    loginPayload.is_new_user,
    loginPayload.isNewAccount,
    loginPayload.IsNewAccount,
    loginPayload.is_new_account,
    loginPayload.accountCreated,
    loginPayload.AccountCreated,
    loginPayload.account_created,
    loginPayload.user?.isNewUser,
    loginPayload.user?.IsNewUser,
    loginPayload.user?.is_new_user,
  ];

  for (const candidate of candidates) {
    const flag = toBooleanFlag(candidate);
    if (flag !== undefined) return flag;
  }

  return undefined;
}

// ── Landing pública ──────────────────────────────────────────────────────────

export function trackLandingView() {
  pushEvent("litterae_landing_view", {
    page_name: "LandingPage",
    page_path: "/leitura-colaborativa",
  });
}

// Mede APENAS o clique no CTA — nenhuma conta foi criada neste ponto.
export function trackCreateAccountClick() {
  pushEvent("litterae_click_create_account", {
    page_name: "LandingPage",
    page_path: "/leitura-colaborativa",
    button_text: "Criar conta",
  });
}

// ── Cadastro ─────────────────────────────────────────────────────────────────

// Cadastro tradicional concluído: chamado somente após o backend confirmar a
// verificação do e-mail. Vale para desktop e mobile.
export function trackCompleteRegistration() {
  pushEvent("litterae_complete_registration", {
    registration_method: "email",
  });
}

// Cadastro via Google concluído.
//
// IMPORTANTE: só dispara quando o backend indica explicitamente que a conta foi
// criada NESTE login (ver resolveIsNewAccount). Login de usuário Google já
// cadastrado não é cadastro e não deve contar como conversão.
//
// Se o backend ainda não expõe esse indicador, o evento deixa de ser enviado e
// um aviso é registrado no console — a ausência do campo é uma pendência de
// backend, não um caso a ser tratado como cadastro por padrão.
export function trackCompleteRegistrationGoogle(loginPayload) {
  const isNewAccount = resolveIsNewAccount(loginPayload);

  if (isNewAccount === undefined) {
    console.warn(
      "[tracking] litterae_complete_registration_google não enviado: o payload " +
        "de login do Google não traz indicador de conta nova (isNewUser/isNewAccount/accountCreated). " +
        "Enquanto o backend não expuser esse campo, cadastros via Google não serão contabilizados.",
    );
    return;
  }

  if (isNewAccount !== true) return;

  pushEvent("litterae_complete_registration_google", {
    registration_method: "google",
  });
}

// ── Primeiro acesso / onboarding ─────────────────────────────────────────────

export function trackFirstAccessView() {
  pushEvent("litterae_first_access_view", {
    page_name: "FirstAccessView",
    page_path: "/dashboard/start",
  });
}

// Chamado somente após o onboarding ser persistido no backend, para que o
// evento não registre uma escolha que não ficou gravada.
export function trackFirstAccessChoice(choice) {
  pushEvent("litterae_first_access_choice", {
    page_name: "FirstAccessView",
    page_path: "/dashboard/start",
    choice,
  });
}

// ── Ativação ─────────────────────────────────────────────────────────────────

// Marca o usuário como ativado — uma única vez na vida, no primeiro texto,
// atividade ou nota que ele criar. Não é um evento por tipo de recurso:
// `activation_type` indica por qual porta a pessoa ativou, e quem já ativou por
// um caminho não dispara de novo ao usar os outros.
//
// A decisão de disparar é de useUserStore._handleFirstActivationEvents, que a
// deriva dos três booleanos permanentes do backend (first_*_created). Sem dedup
// local por localStorage — por isso não re-dispara em outro navegador, aba
// anônima ou storage limpo.
export function trackFirstActivation(activationType) {
  pushEvent("litterae_first_activation", {
    activation_type: activationType,
  });
}

// ── Conversão de pagamento ───────────────────────────────────────────────────

// Dispara o evento de conversão (Google/Meta via dataLayer) indicando que uma
// assinatura foi paga/ativada.
//
// A idempotência ("uma única vez por conversão") NÃO é responsabilidade desta
// função nem do front. O backend é o árbitro único: ele entrega
// `paymentConversionPending` (true/false) no usuário e só permanece true até o
// front confirmar o consumo via acknowledge. O único chamador é
// useUserStore._handlePaymentConversionPending, que dispara este evento apenas
// quando a flag está true e em seguida dá o acknowledge. Por isso aqui a função
// apenas empurra o evento — sem dedup local por localStorage.
//
// O evento de conversão dispara só na primeira assinatura paga da vida do
// usuário, nunca em renovação/upgrade/re-assinatura.
export function trackSubscriptionCompletedOnce(subscription) {
  pushEvent("litterae_subscription_completed", {
    // Prioriza o snapshot da conversão entregue pelo backend (campos
    // paymentConversion*), que captura os dados REAIS daquela conversão no
    // momento da aprovação do pagamento. Sem eles, o fallback genérico (plano
    // atual do perfil / status da conta) reintroduz os bugs: plan_name="unknown"
    // no PIX (corrida de tempo) e subscription_status="authorized" no recorrente
    // (status do preapproval em vez do pagamento).
    plan_name:
      subscription?.paymentConversionPlanName ||
      subscription?.planName ||
      subscription?.plan_name ||
      "unknown",
    plan_id:
      subscription?.paymentConversionPlanId ??
      subscription?.planId ??
      subscription?.plan_id ??
      null,
    subscription_status:
      subscription?.paymentConversionStatus ||
      subscription?.status ||
      "active",
    value:
      subscription?.paymentConversionAmount ||
      subscription?.value ||
      subscription?.price ||
      subscription?.planPrice ||
      subscription?.chargedAmount ||
      subscription?.originalAmount ||
      null,
    currency: "BRL",
  });
}
